import { c as _c } from "react/compiler-runtime";
import { IcoCheckSmall16 } from '@onefootprint/icons';
import { cva, cx } from 'class-variance-authority';
import { AnimatePresence, easeInOut, motion, useReducedMotion } from 'framer-motion';
import { type ReactNode, useEffect, useRef } from 'react';
export type StepState = 'active' | 'complete' | 'ready' | 'locked';
const badge = cva('size-5 shrink-0 rounded-full flex items-center justify-center', {
  variants: {
    state: {
      active: 'bg-accent',
      complete: 'bg-success-inverted',
      ready: 'bg-senary',
      locked: 'bg-senary'
    }
  }
});
const stepNumber = cva('text-label-3', {
  variants: {
    state: {
      active: 'text-quinary',
      complete: 'text-quinary',
      ready: 'text-primary',
      locked: 'text-secondary'
    }
  }
});
const heading = cva('text-label-2', {
  variants: {
    state: {
      active: 'text-primary',
      complete: 'text-primary',
      ready: 'text-primary',
      locked: 'text-tertiary'
    }
  }
});
type StepCardProps = {
  step: number;
  state: StepState;
  title: string;
  hint?: ReactNode;
  aside?: ReactNode;
  focusOnExpand?: boolean;
  children?: ReactNode;
};
export const StepCard = t0 => {
  const $ = _c(36);
  const {
    step,
    state,
    title,
    hint,
    aside,
    focusOnExpand,
    children
  } = t0;
  const shouldReduceMotion = useReducedMotion();
  const headingRef = useRef(null);
  const wasExpanded = useRef(false);
  let t1;
  let t2;
  if ($[0] !== focusOnExpand) {
    t1 = () => {
      if (focusOnExpand && !wasExpanded.current) {
        headingRef.current?.focus();
      }
      wasExpanded.current = focusOnExpand;
    };
    t2 = [focusOnExpand];
    $[0] = focusOnExpand;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  const t3 = state === "active" ? "step" : undefined;
  let t4;
  if ($[3] !== state) {
    t4 = badge({
      state
    });
    $[3] = state;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== state || $[6] !== step) {
    t5 = state === "complete" ? <IcoCheckSmall16 color="quinary" /> : <span className={stepNumber({
      state
    })}>{step}</span>;
    $[5] = state;
    $[6] = step;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t4 || $[9] !== t5) {
    t6 = <div className={t4}>{t5}</div>;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== state) {
    t7 = cx(heading({
      state
    }), "outline-none focus-visible:ring-1 focus-visible:ring-accent");
    $[11] = state;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t7 || $[14] !== title) {
    t8 = <h3 ref={headingRef} tabIndex={-1} className={t7}>{title}</h3>;
    $[13] = t7;
    $[14] = title;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== hint) {
    t9 = hint ? <p className="text-body-3 text-tertiary">{hint}</p> : null;
    $[16] = hint;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t8 || $[19] !== t9) {
    t10 = <div className="flex flex-col gap-0.5 grow basis-0 min-w-0 md:basis-auto">{t8}{t9}</div>;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== aside) {
    t11 = aside ? <div className="flex flex-wrap items-center gap-3 shrink-0 basis-full pl-8 md:flex-nowrap md:basis-auto md:pl-0">{aside}</div> : null;
    $[21] = aside;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t10 || $[24] !== t11 || $[25] !== t6) {
    t12 = <div className="flex flex-wrap items-center gap-3 p-4 md:flex-nowrap">{t6}{t10}{t11}</div>;
    $[23] = t10;
    $[24] = t11;
    $[25] = t6;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== children || $[28] !== shouldReduceMotion) {
    t13 = children ? <motion.div initial={shouldReduceMotion ? false : {
      height: 0,
      opacity: 0
    }} animate={{
      height: "auto",
      opacity: 1
    }} exit={shouldReduceMotion ? {
      opacity: 0
    } : {
      height: 0,
      opacity: 0
    }} transition={{
      duration: shouldReduceMotion ? 0 : 0.2,
      ease: easeInOut
    }} className="overflow-hidden"><div className="flex flex-col gap-4 px-4 pb-4">{children}</div></motion.div> : null;
    $[27] = children;
    $[28] = shouldReduceMotion;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t13) {
    t14 = <AnimatePresence initial={false}>{t13}</AnimatePresence>;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t12 || $[33] !== t14 || $[34] !== t3) {
    t15 = <section aria-current={t3} className="flex flex-col border border-tertiary rounded-default">{t12}{t14}</section>;
    $[32] = t12;
    $[33] = t14;
    $[34] = t3;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  return t15;
};